import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { 
  Bot, 
  Building2, 
  Search, 
  Network, 
  Briefcase, 
  Cpu, 
  TrendingUp,
  Lightbulb,
  GitCompare,
  BarChart3,
  FileText,
  Mail,
  Heart
} from "lucide-react";
import { useRobots } from "@/hooks/useRobots";
import { useCompanies } from "@/hooks/useCompanies";

const quickLinks = [
  { name: "Robots", href: "/robots", icon: Bot },
  { name: "Companies", href: "/companies", icon: Building2 },
  { name: "Ecosystem", href: "/ecosystem", icon: Network },
  { name: "Use Cases", href: "/use-cases", icon: Briefcase },
  { name: "Technology", href: "/technology", icon: Cpu },
  { name: "Market Snapshot", href: "/market-snapshot", icon: TrendingUp },
  { name: "Opportunities", href: "/opportunities", icon: Lightbulb },
  { name: "Compare", href: "/compare", icon: GitCompare },
  { name: "Insights", href: "/insights", icon: BarChart3 },
  { name: "Reports", href: "/reports", icon: FileText },
  { name: "Newsletter", href: "/newsletter", icon: Mail },
  { name: "Contribute", href: "/contribute", icon: Heart },
];

interface SearchCommandProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchCommand({ open, onOpenChange }: SearchCommandProps) {
  const navigate = useNavigate();
  const { data: robots = [] } = useRobots();
  const { data: companies = [] } = useCompanies();

  const runCommand = useCallback((command: () => void) => {
    onOpenChange(false);
    command();
  }, [onOpenChange]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search robots, companies, or navigate..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        
        {/* Quick Navigation */}
        <CommandGroup heading="Quick Navigation">
          {quickLinks.map((link) => (
            <CommandItem
              key={link.href}
              value={link.name}
              onSelect={() => runCommand(() => navigate(link.href))}
            >
              <link.icon className="mr-2 h-4 w-4 text-muted-foreground" />
              <span>{link.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />
        
        {/* Robots */}
        {robots.length > 0 && (
          <CommandGroup heading="Robots">
            {robots.slice(0, 8).map((robot) => (
              <CommandItem
                key={robot.id}
                value={`robot ${robot.name} ${robot.company}`}
                onSelect={() => runCommand(() => navigate(`/robots/${robot.id}`))}
              >
                <Bot className="mr-2 h-4 w-4 text-primary" />
                <div className="flex flex-col">
                  <span>{robot.name}</span>
                  <span className="text-xs text-muted-foreground">{robot.company}</span>
                </div>
              </CommandItem>
            ))}
            {robots.length > 8 && (
              <CommandItem
                value="view all robots"
                onSelect={() => runCommand(() => navigate("/robots"))}
              >
                <Search className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">View all {robots.length} robots...</span>
              </CommandItem>
            )}
          </CommandGroup>
        )}

        <CommandSeparator />
        
        {/* Companies */}
        {companies.length > 0 && (
          <CommandGroup heading="Companies">
            {companies.slice(0, 8).map((company) => (
              <CommandItem
                key={company.id}
                value={`company ${company.name} ${company.country}`}
                onSelect={() => runCommand(() => navigate(`/companies/${company.slug}`))}
              >
                <Building2 className="mr-2 h-4 w-4 text-primary" />
                <div className="flex flex-col">
                  <span>{company.name}</span>
                  <span className="text-xs text-muted-foreground">{company.country}</span>
                </div>
              </CommandItem>
            ))}
            {companies.length > 8 && (
              <CommandItem
                value="view all companies"
                onSelect={() => runCommand(() => navigate("/companies"))}
              >
                <Search className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">View all {companies.length} companies...</span>
              </CommandItem>
            )}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}

export function useSearchCommand() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  return { open, setOpen };
}
